import { useCallback, useRef, useState } from 'react';
import { listCities } from '../../../api';

/**
 * Lazy per-state city loading shared by the map and the list view.
 *   cache[state.id] = { status: 'loading' | 'ok' | 'error', items, error }
 *   ensure(state)   loads a state's cities once (and retries after an error); repeat calls while loading are no-ops
 *   reload(state)   force a fresh load ("Try again"); only the newest request for a state may write its result
 *
 * Deliberately has no "is mounted" bookkeeping: React ignores state updates after unmount, and such
 * flags break under StrictMode's mount -> unmount -> mount cycle (this exact bug hid cities in dev).
 */
export function useCityCache(countryId) {
  const [cache, setCache] = useState({});
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  const requests = useRef({}); // state.id -> number of the newest request

  const reload = useCallback(async (state) => {
    const request = (requests.current[state.id] || 0) + 1;
    requests.current[state.id] = request;
    const isLatest = () => requests.current[state.id] === request;
    setCache((c) => ({ ...c, [state.id]: { status: 'loading', items: [] } }));
    try {
      const items = await listCities(countryId, state.state_id);
      if (isLatest()) setCache((c) => ({ ...c, [state.id]: { status: 'ok', items } }));
    } catch (error) {
      if (isLatest()) setCache((c) => ({ ...c, [state.id]: { status: 'error', items: [], error } }));
    }
  }, [countryId]);

  const ensure = useCallback((state) => {
    const entry = cacheRef.current[state.id];
    const started = Boolean(requests.current[state.id]);
    // `cacheRef` only catches up on the next render, so `started` is what stops a fast double click.
    if (started && !(entry && entry.status === 'error')) return;
    reload(state);
  }, [reload]);

  return { cache, ensure, reload };
}
